import { EditorState, Modifier, ContentState } from "draft-js";
import uuid from "react-native-uuid";

import { DRAFT_ENTITY } from "../../constants/document";

const Diff = require("text-diff");

const createEntity = (currentContent, entityType) => {
  const key = uuid.v4();
  const contentStateWithEntity = currentContent.createEntity(
    entityType,
    "MUTABLE",
    { id: key },
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  return entityKey;
};

const injectEntity = (editorState, diffText) => {
  let currentContent = editorState.getCurrentContent();
  let i = 0;
  for (; i < diffText.length; i += 1) {
    const diff = diffText[i];
    const change = diff[0];
    const text = diff[1];
    let entityType;
    switch (change) {
      case 1:
        entityType = DRAFT_ENTITY.INSERTED;
        break;
      case -1:
        entityType = DRAFT_ENTITY.DELETED;
        break;
      default:
        entityType = null;
    }
    // get anchor and offset for the selection
    currentContent = editorState.getCurrentContent();
    const selection = editorState.getSelection();
    const blockKey = selection.getAnchorKey();
    const blockLen = currentContent.getBlockForKey(blockKey).getText().length;

    const updatedSelection = selection.merge({
      focusKey: blockKey,
      anchorKey: blockKey,
      anchorOffset: blockLen,
      focusOffset: blockLen,
      isBackward: false,
    });

    let entity = null;
    if (entityType !== null) {
      entity = createEntity(currentContent, entityType);
    }
    // create a new content
    const newContentState = Modifier.replaceText(
      currentContent,
      updatedSelection,
      `${text}`,
      null,
      entity,
      null,
    );

    // set the new editor state
    const updatedEditorState = EditorState.set(editorState, {
      currentContent: newContentState,
    });

    editorState = updatedEditorState;
  }

  return editorState;
};

const selectBlock = (editorState, blockKey) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();
  const blockText = currentContent.getBlockForKey(blockKey).getText();
  const updatedSelection = selection.merge({
    anchorKey: blockKey,
    focusKey: blockKey,
    anchorOffset: 0,
    focusOffset: blockText.length,
    isBackward: false,
  });
  return updatedSelection;
};

const applyEntityToBlock = (editorState, blockKey, entityType) => {
  const selection = selectBlock(editorState, blockKey);
  const currentContent = editorState.getCurrentContent();
  const entityKey = createEntity(currentContent, entityType);
  const updatedContent = Modifier.applyEntity(
    currentContent,
    selection,
    entityKey,
  );
  const updatedEditorState = EditorState.set(editorState, {
    currentContent: updatedContent,
  });
  return updatedEditorState;
};

const replaceText = (editorState, diffText) => {
  const selection = editorState.getSelection();
  const currentContent = editorState.getCurrentContent();

  // Remove the modifier content
  const newContentState = Modifier.replaceText(
    currentContent,
    selection,
    ``,
    null,
    null,
    null,
  );

  // set the new editor state
  let updatedEditorState = EditorState.set(editorState, {
    currentContent: newContentState,
  });

  updatedEditorState = EditorState.forceSelection(
    updatedEditorState,
    selection,
  );

  updatedEditorState = injectEntity(updatedEditorState, diffText);

  return updatedEditorState;
};

const applyChanges = (editorState, blockDiffList) => {
  let index = 0;
  const modifiedBlocksKeys = Object.keys(blockDiffList.diff);
  let updatedEditorState = editorState;
  for (; index < modifiedBlocksKeys.length; ) {
    const blockKey = modifiedBlocksKeys[index];
    const contentState = updatedEditorState.getCurrentContent();
    const block = contentState.getBlockForKey(blockKey);

    const blockDiff = blockDiffList.diff[blockKey];
    const { status } = blockDiff;
    if (block === undefined) {
      if (status !== "deleted") {
        index += 1;
      } else {
        let deletedBlock;
        blockDiffList.blocks.forEach((b, index) => {
          if (b.key === blockKey) {
            deletedBlock = {
              index,
              block: b,
            };
          }
        });
        const newBlockMapArray = contentState.getBlockMap().toArray();
        newBlockMapArray.splice(deletedBlock.index, 0, deletedBlock.block);
        updatedEditorState = EditorState.push(
          updatedEditorState,
          ContentState.createFromBlockArray(newBlockMapArray),
        );
      }
    }

    let change;
    let updatedSelection;
    switch (status) {
      case "changed":
        change = blockDiff.diff;
        updatedSelection = selectBlock(updatedEditorState, blockKey);
        updatedEditorState = EditorState.forceSelection(
          updatedEditorState,
          updatedSelection,
        );

        updatedEditorState = replaceText(updatedEditorState, change);
        break;
      case "added":
        updatedEditorState = applyEntityToBlock(
          updatedEditorState,
          blockKey,
          DRAFT_ENTITY.INSERTED,
        );
        break;
      case "deleted":
        updatedEditorState = applyEntityToBlock(
          updatedEditorState,
          blockKey,
          DRAFT_ENTITY.DELETED,
        );
        break;
      default:
        change = [
          ["0", blockDiffList.blocks.find(item => item.key === blockKey).text],
        ];
        updatedEditorState = replaceText(updatedEditorState, change);
    }
    index += 1;
  }
  return updatedEditorState;
};

const getBlocksDiff = (oldContent, newContent) => {
  const blocksDiff = {};
  const changedBlocks = [];
  let newKey = null;
  let oldKey = null;
  // get old blocks and new blocks
  if (newContent !== undefined && newContent.getFirstBlock() !== undefined) {
    newKey = newContent.getFirstBlock().getKey();
  }
  if (oldContent !== undefined && oldContent.getFirstBlock() !== undefined) {
    oldKey = oldContent.getFirstBlock().getKey();
  }
  // get the modified blocks
  for (
    ;
    newKey !== null &&
    newKey !== undefined &&
    oldKey !== null &&
    oldKey !== undefined;

  ) {
    if (oldKey === newKey) {
      const oldText = oldContent.getBlockForKey(oldKey).getText();
      const newText = newContent.getBlockForKey(newKey).getText();
      const diff = new Diff();
      const textDiff = diff.main(oldText, newText);
      if (!(Object.keys(textDiff).length === 1 && textDiff[0][0] === 0)) {
        diff.cleanupSemantic(textDiff);
        blocksDiff[oldKey] = {
          status: "changed",
          diff: textDiff,
        };
      }
      changedBlocks.push(newContent.getBlockForKey(newKey));
      newKey = newContent.getKeyAfter(newKey);
      oldKey = oldContent.getKeyAfter(oldKey);
    } else if (oldContent.getBlockForKey(newKey) === undefined) {
      // this block has been added to content
      blocksDiff[newKey] = {
        status: "added",
      };
      changedBlocks.push(newContent.getBlockForKey(newKey));
      newKey = newContent.getKeyAfter(newKey);
    } else if (newContent.getBlockForKey(oldKey) === undefined) {
      // this block has been deleted from the content
      blocksDiff[oldKey] = {
        status: "deleted",
      };
      changedBlocks.push(oldContent.getBlockForKey(oldKey));
      oldKey = oldContent.getKeyAfter(oldKey);
    }
  }
  for (; oldKey !== null && oldKey !== undefined; ) {
    // this block has been deleted from the content
    blocksDiff[oldKey] = {
      status: "deleted",
    };
    changedBlocks.push(oldContent.getBlockForKey(oldKey));
    oldKey = oldContent.getKeyAfter(oldKey);
  }

  for (; newKey !== null && newKey !== undefined; ) {
    // this block has been deleted from the content
    blocksDiff[newKey] = {
      status: "added",
    };
    changedBlocks.push(newContent.getBlockForKey(newKey));
    newKey = newContent.getKeyAfter(newKey);
  }
  return { diff: blocksDiff, blocks: changedBlocks };
};

const getEditorDiff = (currentData, oldData) => {
  let newContentState;
  let oldContentState;
  // get current content
  if (currentData instanceof EditorState) {
    newContentState = currentData.getCurrentContent();
  } else if (currentData instanceof ContentState) {
    newContentState = currentData;
  }

  // get old content
  if (oldData instanceof EditorState) {
    oldContentState = oldData.getCurrentContent();
  } else if (oldData instanceof ContentState) {
    oldContentState = oldData;
  }

  // get diff list block by block
  const diffList = getBlocksDiff(oldContentState, newContentState);

  let editorState =
    currentData instanceof EditorState
      ? currentData
      : EditorState.createWithContent(currentData);
  editorState = applyChanges(editorState, diffList);
  return editorState;
};

export default getEditorDiff;



// WEBPACK FOOTER //
// ./src/components/RevvEditor/getEditorDiff.js